import React from 'react';
import { tr } from '../../i18n/i18n';
import { useNavigate } from 'react-router-dom';
import { APP_ROUTES } from '../../constants';
import { AppButton } from '../atoms';
import { SEVERITY_STYLES } from '../../constant/main.constant';
import { useTranslation } from 'react-i18next';

interface ExpiredSubscriptionOverlayProps {
  canManageBilling: boolean;
  unpaidInvoiceCount: number;
  outstandingAmount: number;
  className?: string;
}

const ExpiredSubscriptionOverlay: React.FC<ExpiredSubscriptionOverlayProps> = ({
  canManageBilling,
  unpaidInvoiceCount,
  outstandingAmount,
  className = 'fixed inset-0',
}) => {
  const navigate = useNavigate();
  const severity = canManageBilling ? 'danger' : 'warning';
  const styles = SEVERITY_STYLES[severity];

  const handlePayNow = () => {
    navigate(`${APP_ROUTES.BILLING_AND_PLANS}?tab=billing`);
  };

  const formatCurrency = (amount: number) =>
    new Intl.NumberFormat('en-LK', {
      style: 'currency',
      currency: 'LKR',
      minimumFractionDigits: 0,
    }).format(amount || 0);

  const { t } = useTranslation();
  const title = canManageBilling ? tr('g.service_restricted', 'Service Restricted') : tr('g.subscription_inactive', 'Subscription Inactive');
  const overlayMessage = canManageBilling
    ? t(
        'subscription_overlay.restricted',
        'Service Restricted: {{count}} overdue invoice(s) totaling {{amount}}. Your access has been restricted. Please settle all outstanding invoices immediately to restore full access.',
        { count: unpaidInvoiceCount, amount: formatCurrency(outstandingAmount) },
      )
    : t(
        'subscription_overlay.inactive',
        "Your institute's subscription is currently inactive. Please contact your school administrator or billing department to restore full access to your workspace tools.",
      );
  return (
    <div
      className={`${className} z-[1] flex h-full items-center justify-center bg-white/45 backdrop-blur-sm`}
    >
      <div className="fixed left-1/2 top-1/2 z-[1001] w-[calc(100%-2rem)] max-w-md -translate-x-1/2 -translate-y-1/2 rounded-2xl border border-gray-200 bg-white px-6 py-8 shadow-2xl sm:px-8">
        <div className="flex flex-col items-center justify-center gap-4 p-1 text-center sm:p-2">
          <div
            className={`aspect-square rounded-full border-2 p-3 shadow-lg ${styles.bgColor} ${styles.iconBorder}`}
          >
            <div className="flex h-full w-full items-center justify-center">
              <i className={`pi pi-lock text-3xl ${styles.iconColor}`} aria-hidden="true" />
            </div>
          </div>

          <h3 className="font-poppins text-lg font-semibold text-gray-800">{title}</h3>

          <p className="max-w-xl font-poppins text-sm leading-6 text-gray-700 sm:text-base">
            {overlayMessage}
          </p>

          {canManageBilling && (
            <div className="mt-2 flex w-full justify-center">
              <AppButton label={t('common.pay_now', 'Pay Now')} onClick={handlePayNow} severity="danger" />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export type { ExpiredSubscriptionOverlayProps };
export default ExpiredSubscriptionOverlay;